/*
 * Contact search form modal
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2021 Gallagher Bassett Services, Inc.
 */

import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ContactService, JurisdictionService} from 'app/shared/services';
import {take} from 'rxjs/operators';
import {Contact, ContactType, Enum} from 'app/shared/models';
import {NotificationService} from 'app/shared/services/notification.service';

@Component ( {
    selector: 'reporter-contacts-search',
    templateUrl: './contacts-search.component.html',
    styleUrls: ['./contacts-search.component.scss'],
    standalone: false
} )
export class ContactsSearchComponent implements OnChanges, OnInit {

  @Input () show = false;
  @Input () contactType: ContactType;
  @Output () close = new EventEmitter<boolean> ();
  @Output () selectContact = new EventEmitter<Contact> ();

  contacts: Contact[];
  searchModel = {};
  jurisdictions: Enum[];
  pageNumber = 1;
  totalRecords = 0;
  totalPages = 0;
  details: Contact;
  timeOut;
  selectedOnHover = false;
  inFocus: Contact;
  contactsSearchOpen = true;

  constructor(
    private contactService: ContactService,
    private jurisdictionService: JurisdictionService,
    private notificationService: NotificationService,
  ) {
  }

  ngOnInit() {
    this.jurisdictionService.allByCountry ()
      .pipe ( take ( 1 ) )
      .subscribe (
        jurisdictions => this.jurisdictions = jurisdictions
      );
  }

  ngOnChanges( changes: SimpleChanges ): void {
  }

  search() {
    this.notificationService.clearAll ();
    this.contacts = undefined;
    this.pageNumber = 1;

    this.contactService.search ( this.searchModel, this.contactType, this.pageNumber )
      .pipe ( take ( 1 ) )
      .subscribe ( result => {
        this.contacts = result.records;
        this.totalPages = result.totalPages;
        this.totalRecords = result.totalRecords;
        this.pageNumber = result.currentPage;
        if ( !this.contacts?.length ) {
          this.contactsSearchOpen = true;
          this.notificationService.addNotification ( 'warning', 'No records found.' );
        } else {
          this.contactsSearchOpen = false;
        }
      } );
  }

  loadNextPage() {
    this.pageNumber++;
    this.contactService.search ( this.searchModel, this.contactType, this.pageNumber )
      .pipe ( take ( 1 ) )
      .subscribe ( results => {
        this.contacts = [ ...this.contacts, ...results.records ];
      } );
  }

  async getDetails( c: Contact ) {
    this.selectedOnHover = false;
    if ( c?.id === this.details?.id ) {
      this.details = undefined;
      this.inFocus = undefined;
    } else {
      this.details = { ...c };
    }
  }

  mouseOver( c: Contact ) {
    if ( c?.id !== this.details?.id ) {
      if ( this.timeOut ) {
        clearTimeout ( this.timeOut );
      }
      this.timeOut = setTimeout ( async () => {
        await this.getDetails ( c );
        this.selectedOnHover = true;
      }, 2000 );
    }
  }

  unsetTimeOut( c: Contact ) {
    if ( this.timeOut ) {
      clearTimeout ( this.timeOut );
    }
    if ( this.details && c?.id === this.details?.id && this.selectedOnHover ) {
      this.selectedOnHover = false;
      this.details = undefined;
      this.inFocus = undefined;
    }
  }

  select( c: Contact ) {
    this.selectContact.emit ( c );
    this.inFocus = undefined;
  }

  resetForm() {
    this.searchModel = {};
    this.contacts = undefined;
    this.notificationService.clearAll ();
  }
}
